import { useState } from "react";
import CrudTable from "../components/CrudTable";
import PDFButton from "../components/PDFButton";
import { api, BACKEND_URL } from "../lib/api";
import { Download, Eye, FileText } from "lucide-react";

const TABS = [
  { key: "federal", label: "Federales" },
  { key: "state", label: "Estatales" },
  { key: "municipal", label: "Municipales" },
  { key: "client", label: "Clientes" },
  { key: "reports", label: "Reportes Ejecutivos" },
];

const FIELDS = [
  { key: "title", label: "Título", required: true, full: true },
  { key: "agency", label: "Dependencia / Cliente" },
  { key: "signer", label: "Firmante" },
  { key: "version", label: "Versión" },
  { key: "issue_date", label: "Fecha emisión", type: "date" },
  { key: "next_review", label: "Próxima revisión", type: "date" },
  { key: "format_type", label: "Tipo de archivo", type: "select", options: ["Word", "Excel", "PowerPoint", "PDF", "Foto", "Otro"] },
  { key: "status", label: "Status", type: "select", options: ["Vigente", "Borrador", "Obsoleto"] },
  { key: "comments", label: "Comentarios", type: "textarea", full: true },
];

export default function Formats() {
  const [tab, setTab] = useState("federal");
  return (
    <div data-testid="formats-module">
      <div className="flex items-center justify-between mb-3">
        <h1 className="card-h1" style={{ margin: 0 }}>Formatos</h1>
        <PDFButton endpoint="/pdf/module/racs" filename={`formatos_${tab}.pdf`} label="Reporte PDF" />
      </div>
      <p className="text-xs text-dim mb-3">
        Repositorio de formatos federales, estatales, municipales y de clientes. Click en una fila para previsualizar el documento y doble click para descargarlo.
      </p>
      <div className="tabs-bar">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`tab ${tab === t.key ? "active" : ""}`}
            onClick={() => setTab(t.key)}
            data-testid={`formats-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab !== "reports" && (
        <CrudTable
          key={tab}
          collection="formats"
          testIdPrefix={`formats-${tab}`}
          filters={{ scope: tab }}
          fields={[
            { key: "scope", label: "Ámbito", default: tab, hiddenInForm: true, hidden: true },
            ...FIELDS,
          ]}
        />
      )}
      {tab === "reports" && <ExecutivePPTPanel />}
    </div>
  );
}

function ExecutivePPTPanel() {
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState(""); // "generate" | "preview" | "download" | ""
  const [pptBlob, setPptBlob] = useState(null);   // editable .pptx cached
  const [pdfBlob, setPdfBlob] = useState(null);   // PDF preview cached
  const [pdfUrl, setPdfUrl] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);

  async function fetchReport(format) {
    const token = localStorage.getItem("token");
    const audit = localStorage.getItem("audit_tenant_id");
    const headers = { Authorization: `Bearer ${token}` };
    if (audit) headers["X-Audit-Tenant-Id"] = audit;
    const res = await fetch(`${BACKEND_URL}/api/pptx/executive-cotemar?format=${format}`, { headers });
    if (!res.ok) throw new Error(await res.text());
    return await res.blob();
  }

  async function generateReport() {
    setBusy(true); setStage("generate");
    try {
      // Fetch both PPTX (editable) and PDF (preview) — sequential to keep server load sane.
      const ppt = await fetchReport("pptx");
      const pdf = await fetchReport("pdf");
      setPptBlob(ppt);
      setPdfBlob(pdf);
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
      setPdfUrl(URL.createObjectURL(new Blob([pdf], { type: "application/pdf" })));
      setGeneratedAt(new Date());
    } catch (e) {
      alert("No se pudo generar el reporte: " + e.message);
    } finally { setBusy(false); setStage(""); }
  }

  function previewReport() {
    if (!pdfUrl) { alert("Primero genera el reporte."); return; }
    window.open(pdfUrl, "_blank");
  }

  function downloadEditable() {
    if (!pptBlob) { alert("Primero genera el reporte."); return; }
    const url = URL.createObjectURL(pptBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Reporte_Ejecutivo_SGA_Cotemar_${new Date().toISOString().slice(0, 10)}.pptx`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 500);
  }

  const ready = !!(pptBlob && pdfBlob);

  return (
    <div className="card mt-4" data-testid="exec-ppt-panel" style={{ background: "rgba(245,184,46,0.06)", border: "1px solid rgba(245,184,46,0.4)" }}>
      <div className="card-title">Reporte Ejecutivo PPT — 12 slides editables</div>
      <p className="text-xs text-dim mb-3">
        Genera un reporte ejecutivo ambiental en PowerPoint con 12 slides editables: portada, panel principal
        en 2 slides ejecutivas (indicadores clave + resumen ambiental + acciones prioritarias y riesgos),
        consumo de recursos, aguas residuales, residuos, emisiones, biodiversidad, cumplimiento, cierre y
        análisis experto con IA. Datos reales del Dashboard del tenant. La slide 12 usa Claude Sonnet 4.5
        vía Emergent para el diagnóstico técnico; si no está disponible, se usa el análisis experto de respaldo.
      </p>
      <div className="flex gap-2 flex-wrap items-center">
        <button
          className="btn btn-primary"
          onClick={generateReport}
          disabled={busy}
          data-testid="exec-ppt-generate"
          title="Genera el reporte ejecutivo y prepara la previsualización y descarga"
        >
          {busy && stage === "generate" ? <span className="spinner" /> : <><FileText size={14} /> Generar reporte</>}
        </button>
        <button
          className="btn"
          onClick={previewReport}
          disabled={!ready || busy}
          data-testid="exec-ppt-preview"
          title={ready ? "Abre el PDF de previsualización en una nueva pestaña" : "Primero genera el reporte"}
        >
          <Eye size={14} /> Previsualizar
        </button>
        <button
          className="btn"
          onClick={downloadEditable}
          disabled={!ready || busy}
          data-testid="exec-ppt-download"
          title={ready ? "Descarga el archivo .pptx editable" : "Primero genera el reporte"}
        >
          <Download size={14} /> Descargar PPT editable
        </button>
        {generatedAt && (
          <span className="text-xs text-dim" data-testid="exec-ppt-generated-at">
            Generado: {generatedAt.toLocaleTimeString("es-MX")}
          </span>
        )}
      </div>
      {pdfUrl && (
        <div className="mt-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs text-dim">Vista previa inline (PDF generado desde el PPTX). El archivo editable .pptx mantiene shapes/tablas/charts.</div>
            <button className="btn btn-sm" onClick={() => window.open(pdfUrl, "_blank", "noopener,noreferrer")} data-testid="exec-ppt-open-tab">
              Abrir en nueva pestaña
            </button>
          </div>
          <object
            data={pdfUrl}
            type="application/pdf"
            data-testid="exec-ppt-preview-frame"
            style={{ width: "100%", height: "75vh", border: "1px solid var(--border-2)", borderRadius: 8, background: "#fff" }}
          >
            <iframe
              src={pdfUrl}
              title="Vista previa Reporte Ejecutivo"
              style={{ width: "100%", height: "75vh", border: "none", background: "#fff" }}
            />
          </object>
        </div>
      )}
    </div>
  );
}
